import { FaroMenuItem } from "@components/common/faro-select/faro-menu-item";
import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { FaroTextMenuButton } from "@components/common/faro-text-menu-button";
import {
  getRoleMenuItemSx,
  RoleMenuItem,
} from "@components/role/role-menu-item";
import {
  ChangeRoleEntityType,
  useChangeRoleBulkDialogContent,
} from "@components/table/members/bulk-actions/bulk-change-role-helper";
import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";
import { BaseMemberAndTeamProps } from "@custom-types/member-types";
import { FaroCoreApiConst, FaroCoreApiTypes } from "@stellar/core-api-client";
import { updateProjectRoleInMap } from "@store/company-member-groups/company-member-groups-slice";
import { updateMemberRoleInProjectBulk } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  bulkActionNameSelector,
  selectedRowIdsSelector,
  shouldShowSuccessDialogSelector,
} from "@store/table/table-selector";
import { setBulkActionName } from "@store/table/table-slice";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect, useMemo, useState } from "react";
import { Text, UserRoleIcon } from "@flat-ui";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { useCompanyId } from "@context-providers/company-id-provider";

interface Props extends BaseMemberAndTeamProps {
  /** The projects currently shown in the group */
  projects: FaroCoreApiTypes.ProjectSummary[];
}

/** Bulk action button that opens a role dropdown to change a member's role in selected projects */
export function BulkChangeGroupProjectRole({
  member,
  projects,
}: Props): JSX.Element {
  const companyId = useCompanyId();
  const dispatch = useAppDispatch();
  const mode = useBulkActionsMode();

  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const [newRole, setNewRole] =
    useState<FaroCoreApiTypes.ProjectRole>("RESOURCE_VIEWER");

  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const shouldShowSuccessDialog = useAppSelector(
    shouldShowSuccessDialogSelector
  );
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);

  const selectedProjects = useMemo(
    () => projects.filter((p) => selectedRowIds.includes(p.id)),
    [projects, selectedRowIds]
  );

  const { bulkDialogContent, successDialogContent } =
    useChangeRoleBulkDialogContent({
      subjectType: "project",
      newRole,
      entityType: ChangeRoleEntityType.project,
    });

  useEffect(() => {
    if (shouldShowSuccessDialog) {
      setShouldShowDialog(false);
    }
  }, [shouldShowSuccessDialog]);

  function onNewRoleClicked(event: React.MouseEvent<HTMLLIElement>): void {
    dispatch(setBulkActionName("changeMemberProjectRole"));
    setNewRole(
      event.currentTarget.dataset.myValue as FaroCoreApiTypes.ProjectRole
    );
    setShouldShowDialog(true);
  }

  async function onChangeRoleConfirmed(
    project: FaroCoreApiTypes.ProjectSummary
  ): Promise<void> {
    await dispatch(
      updateMemberRoleInProjectBulk({
        companyId,
        projectId: project.id,
        userIdentity: member.identity,
        payload: { role: newRole },
      })
    ).unwrap();
    dispatch(updateProjectRoleInMap({ projectId: project.id, newRole }));
  }

  const config = getBulkActionButtonConfig(mode, "Change role");

  return (
    <>
      <FaroTextMenuButton
        buttonTitle={
          config.shouldShowText ? (
            <Text variant="buttonM">Change role</Text>
          ) : undefined
        }
        tooltipText={config.tooltipText}
        menuPosition={{
          anchorOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
        }}
        rippleColor={config.rippleColor}
        shouldShowEndIcon={false}
        buttonSx={() => config.sx}
        startIcon={<UserRoleIcon style={{ width: 18, height: 18 }} />}
      >
        {FaroCoreApiConst.allProjectRoles.map(
          (role) =>
            role !== "RESOURCE_OWNER" && (
              <FaroMenuItem
                data-my-value={role}
                key={role}
                value={role}
                onClick={onNewRoleClicked}
                sx={getRoleMenuItemSx({ isTableCell: false })}
              >
                <RoleMenuItem role={role} />
              </FaroMenuItem>
            )
        )}
      </FaroTextMenuButton>

      {bulkActionName === "changeMemberProjectRole" &&
        shouldShowSuccessDialog && (
          <BulkSuccessDialog dialogContent={successDialogContent} />
        )}

      {bulkActionName === "changeMemberProjectRole" && shouldShowDialog && (
        <FaroBulkDialog<FaroCoreApiTypes.ProjectSummary>
          dialogContent={bulkDialogContent}
          onClose={() => setShouldShowDialog(false)}
          trackingEvent={BulkActionEvents.bulkChangeMemberProjectRole}
          bulkActionCallback={onChangeRoleConfirmed}
          selectedEntities={selectedProjects}
          uniqueIdKey="id"
          tableDataType="projects"
        />
      )}
    </>
  );
}
